import { arcgisToGeoJSON } from 'arcgis-to-geojson-utils';
import { useEffect, useState, useCallback, useContext } from 'react';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import config from '../../../config';
import { FeatureCollection } from 'geojson';
import { NotificationsContext } from '../../../providers/NotificationsProvider';
import { useTranslation } from '../../../hooks/useTranslation';
import { PSPSZoneAttributes } from '../types';
import { featureFlagsContext } from '../../../providers/FeatureFlagsProvider';

interface Props {
  shouldFetch: boolean;
}
const ARCGIS_URL = config.amazonLocationService.arcgisPSPSURL;

export function usePspsArcgisData({ shouldFetch }: Props) {
  const [geojson, setGeojson] = useState<FeatureCollection | null>(null);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const { wrapWithLoader } = useWrapWithLoader();
  const notification = useContext(NotificationsContext);
  const { t } = useTranslation();

  const { isOutagesPSPSEnabled, loading: featureFlagLoading } = useContext(
    featureFlagsContext,
  );

  const fetchArcGISData = useCallback(
    wrapWithLoader(async () => {
      try {
        const res = await fetch(ARCGIS_URL);
        const data = await res.json();

        const features = data.features.map((f: any) => {
          const geo = {
            ...arcgisToGeoJSON(f),
            properties: f.attributes as PSPSZoneAttributes,
          };
          return geo;
        });

        setGeojson({
          type: 'FeatureCollection',
          features,
        });
        setIsDataFetched(true);
      } catch (err) {
        notification.setState({
          isOpen: true,
          severity: 'error',
          message: t('UNABLE_TO_FETCH_PSPS_DATA'),
        });
      }
    }),
    [],
  );

  useEffect(() => {
    if (
      shouldFetch &&
      !isDataFetched &&
      !featureFlagLoading &&
      isOutagesPSPSEnabled
    ) {
      void fetchArcGISData();
    }
  }, [
    shouldFetch,
    isDataFetched,
    fetchArcGISData,
    featureFlagLoading,
    isOutagesPSPSEnabled,
  ]);

  return {
    pspsGeoJsonData: geojson,
  };
}
